import React, { useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import FormRenderer from './FormRenderer'
import FormTemplateField from './FormTemplateField'
import { SCORED_QUESTION_OPTIONS } from './AuditQuestionField'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import CloseIcon from '@mui/icons-material/Close'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import AddIcon from '@mui/icons-material/Add'
import SettingsIcon from '@mui/icons-material/Settings'
import RepeatIcon from '@mui/icons-material/Repeat'
import RuleIcon from '@mui/icons-material/Rule'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ApartmentIcon from '@mui/icons-material/Apartment'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SearchableSelect from 'components/SearchableSelect'
import { FORM_CATEGORIES } from '../formCategories'

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'is exactly' },
  { value: 'not_equals', label: 'is not' },
  { value: 'greater_than', label: 'is more than' },
  { value: 'greater_or_equal', label: 'is at least' },
  { value: 'less_than', label: 'is less than' },
  { value: 'less_or_equal', label: 'is at most' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'is_empty', label: 'was left blank' },
  { value: 'is_not_empty', label: 'was answered' }
]

const CONDITION_ACTIONS = [
  { value: 'show', label: 'Show this question' },
  { value: 'hide', label: 'Hide this question' },
  { value: 'require', label: 'Make this question required' }
]

const ANSWERLESS_OPERATORS = ['is_empty', 'is_not_empty']

const readableAnswer = (raw, sourceField) => {
  if (!sourceField) return String(raw)
  if (sourceField.type === 'yes_no') {
    if (String(raw) === 'yes') return 'Yes'
    if (String(raw) === 'no') return 'No'
  }
  const match = (sourceField.options || []).find(
    option => String(option.value) === String(raw)
  )
  return match ? (match.label || match.value) : String(raw)
}

const joinAnswers = answers => {
  if (answers.length <= 1) return answers[0] || ''
  return `${answers.slice(0, -1).join(', ')} or ${answers[answers.length - 1]}`
}

const describeCondition = (cond, sourceField) => {
  if (!cond?.fieldKey || !cond?.operator) return null

  const label = (sourceField?.label || '').trim()
  const question = label ? `\u201C${label}\u201D` : 'an earlier question'
  const phrase = (CONDITION_OPERATORS.find(op => op.value === cond.operator) || {}).label
  if (!phrase) return null

  if (ANSWERLESS_OPERATORS.includes(cond.operator)) {
    return `${question} ${phrase}`
  }

  const answers = isListOperator(cond.operator)
    ? toConditionList(cond.value).map(entry => readableAnswer(entry, sourceField))
    : [toConditionScalar(cond.value)].filter(entry => entry !== '')
        .map(entry => readableAnswer(entry, sourceField))

  if (answers.length === 0) return null

  return `${question} ${phrase} ${joinAnswers(answers)}`
}

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Short text. One line answer' },
  { value: 'textarea', label: 'Long text. Multi-line answer' },
  { value: 'number', label: 'Number. Numeric value' },
  { value: 'date', label: 'Date. Pick a date' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'checkbox', label: 'Tick box. Confirm or agree' },
  { value: 'select', label: 'Choose one. Dropdown list' },
  { value: 'multiselect', label: 'Choose many. Multiple selections' },
  { value: 'file', label: 'File upload. Attach a document or photo' },
  { value: 'image', label: 'Image. Photo capture or upload' },
  { value: 'video', label: 'Video. Record or upload a clip' },
  // GPS is captured automatically on every form save/submit (see the
  // submitGeolocation flow in FormRenderer), so it's no longer an
  // authoring choice.
  { value: 'signature', label: 'Signature. Draw to sign' },
  { value: 'audit_question', label: 'Audit question. Yes / No / N/A with comments, actions and photos' },
  { value: 'scored_question', label: 'Scored question. Score 0 to 2 or N/A, with comments, actions and photos' },
  { value: 'statement', label: 'Statement. Text to read, no answer needed' },
  { value: 'email', label: 'Email address' },
  { value: 'phone', label: 'Phone number' }
]

const LIST_OPERATORS = ['in', 'not_in']

const isListOperator = operator => LIST_OPERATORS.includes(operator)

const toConditionList = value => {
  if (Array.isArray(value)) {
    return value.map(entry => String(entry).trim()).filter(entry => entry !== '')
  }
  if (value === null || value === undefined || value === '') return []
  return String(value).split(',').map(entry => entry.trim()).filter(entry => entry !== '')
}

const toConditionScalar = value => {
  if (Array.isArray(value)) return value.length ? String(value[0]) : ''
  return value === null || value === undefined ? '' : String(value)
}

const normaliseConditionValue = (operator, value) =>
  isListOperator(operator) ? toConditionList(value) : toConditionScalar(value)

const RuleSummary = ({ parts, logic, subject, action }) => {
  const clauses = (parts || []).filter(Boolean)
  if (clauses.length === 0) return null

  const joined = clauses.join(logic === 'or' ? ' or ' : ' and ')
  const lead = action === 'hide'
    ? `This ${subject} is shown by default. It will be hidden when `
    : action === 'require'
      ? `This ${subject} must be answered when `
      : `This ${subject} stays hidden. It will appear when `

  return (
    <RuleSummaryBox>
      <RuleSummaryHeading>In plain English</RuleSummaryHeading>
      <RuleSummaryText>
        {lead}<strong>{joined}</strong>.
        {clauses.length > 1 && (
          <RuleSummaryAside>
            {logic === 'or'
              ? ' Any one of those is enough.'
              : ' All of those must be true at the same time.'}
          </RuleSummaryAside>
        )}
      </RuleSummaryText>
    </RuleSummaryBox>
  )
}

const generateKey = prefix =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`

const inferFileAcceptMode = (acceptedMimeTypes) => {
  if (!acceptedMimeTypes || acceptedMimeTypes.length === 0) return 'files_and_photos'
  if (acceptedMimeTypes.length === 1 && acceptedMimeTypes[0] === 'image/*') return 'photos_only'
  return 'files_and_photos'
}

const createEmptyField = () => ({
  key: generateKey('field'),
  type: 'text',
  label: '',
  placeholder: '',
  description: '',
  required: false,
  readOnly: false,
  options: [],
  validation: {},
  conditionals: []
})

const createEmptySection = () => ({
  key: generateKey('section'),
  title: '',
  description: '',
  position: 0,
  repeatable: false,
  minEntries: 1,
  maxEntries: null,
  fields: [createEmptyField()]
})

// Fields sourced from the Building Passport (populated at task creation via auto_populate_map)
const BP_AUTO_FIELDS = [
  { key: 'bp_building_name', label: 'Building Name', type: 'text', source: 'name' },
  { key: 'bp_building_street', label: 'Street', type: 'text', source: 'street' },
  { key: 'bp_building_city', label: 'City', type: 'text', source: 'city' },
  { key: 'bp_building_postal_code', label: 'Postal Code', type: 'text', source: 'postal_code' },
  { key: 'bp_number_of_floors', label: 'Number of Floors', type: 'number', source: 'floors_number' },
  { key: 'bp_building_address', label: 'Building Address', type: 'text', source: 'address' },
  { key: 'bp_number_of_units', label: 'Number of Units', type: 'number', source: 'number_of_units' },
  { key: 'bp_unit_name',     label: 'Unit Name',     type: 'text',   source: 'unit_name' },
  { key: 'bp_floor_numbers', label: 'Floor Level(s)', type: 'text',   source: 'unit_floor_numbers' }
]

// Fields sourced from the task context (populated dynamically at render time)
const TASK_CONTEXT_FIELDS = [
  {
    key: 'task_creator',
    label: 'Form Creator',
    type: 'text',
    taskSource: 'creator'
  },
  {
    key: 'task_date_created',
    label: 'Date Created',
    type: 'date',
    taskSource: 'createdAt'
  },
  {
    key: 'task_assigned_to',
    label: 'Assigned To',
    type: 'text',
    taskSource: 'assignees'
  },
  {
    key: 'task_date_submitted',
    label: 'Date Submitted',
    type: 'date',
    taskSource: 'submittedAt'
  },
  {
    key: 'task_date_approved',
    label: 'Date Approved',
    type: 'date',
    taskSource: 'approvedAt'
  }
]

const createBpInfoSection = () => ({
  key: generateKey('section'),
  title: 'Building & Task Information',
  description: 'Building fields are auto-populated from the Building Passport. Task fields (creator, dates, assignees) are filled automatically as the task progresses.',
  position: 0,
  repeatable: false,
  minEntries: 1,
  maxEntries: null,
  fields: [
    ...BP_AUTO_FIELDS.map(f => ({
      key: f.key,
      type: f.type,
      label: f.label,
      placeholder: '',
      required: false,
      readOnly: true,
      autoPopulated: true,
      autoPopulateSource: f.source,
      options: [],
      validation: {},
      conditionals: []
    })),
    ...TASK_CONTEXT_FIELDS.map(f => ({
      key: generateKey('field'),
      type: f.type,
      label: f.label,
      placeholder: '',
      required: false,
      readOnly: true,
      autoPopulated: true,
      taskContextSource: f.taskSource,
      options: [],
      validation: {},
      conditionals: []
    }))
  ]
})

// --- Sub-components ---

const FieldEditor = ({ field, onUpdate, onRemove, onDuplicate, isOnly, readOnly = false, allFields = [], otherSections = [], onDuplicateToSection }) => {
  const [showConditionals, setShowConditionals] = useState(true)
  const [showCopyMenu, setShowCopyMenu] = useState(false)
  const needsOptions = ['select', 'multiselect', 'radio'].includes(field.type)

  const updateField = (key, value) => onUpdate && onUpdate({ ...field, [key]: value })

  // All fields except current one can be condition sources
  const availableSourceFields = allFields.filter(f => f.key !== field.key)

  // Get the source field for a conditional (to show its options if applicable)
  const getSourceField = fieldKey => allFields.find(f => f.key === fieldKey)

  const addConditional = () => {
    const conditionals = [
      ...(field.conditionals || []),
      {
        fieldKey: '',
        operator: 'equals',
        value: '',
        action: 'show',
        required: true
      }
    ]
    updateField('conditionals', conditionals)
  }

  const updateConditional = (index, key, value) => {
    const conditionals = [...(field.conditionals || [])]
    const next = { ...conditionals[index], [key]: value }
    if (key === 'fieldKey') {
      next.value = isListOperator(next.operator) ? [] : ''
    } else if (key === 'operator') {
      next.value = normaliseConditionValue(value, conditionals[index]?.value)
    }
    conditionals[index] = next
    updateField('conditionals', conditionals)
  }

  const removeConditional = index => {
    const conditionals = (field.conditionals || []).filter(
      (_, i) => i !== index
    )
    updateField('conditionals', conditionals)
  }

  const addOption = () => {
    const options = [...(field.options || []), { value: '', label: '' }]
    updateField('options', options)
  }

  const updateOption = (index, key, value) => {
    const options = [...(field.options || [])]
    options[index] = { ...options[index], [key]: value }
    updateField('options', options)
  }

  const removeOption = index => {
    const options = (field.options || []).filter((_, i) => i !== index)
    updateField('options', options)
  }


  return (
    <FieldCard $readOnly={readOnly} $autoPopulated={field.autoPopulated}>
      {field.autoPopulated && field.autoPopulateSource && (
        <AutoPopulatedBadge>
          <ApartmentIcon style={{ fontSize: 13 }} />
          Auto-populated from Building Passport
        </AutoPopulatedBadge>
      )}
      {field.autoPopulated && field.taskContextSource && (
        <TaskContextBadge>
          Filled from task data
        </TaskContextBadge>
      )}
      <FieldRow>
        <FieldGroup $flex={1}>
          <FieldLabel>Question</FieldLabel>
          <Input
            type='text'
            value={field.label}
            onChange={e => !readOnly && updateField('label', e.target.value)}
            placeholder='Enter question'
            readOnly={readOnly}
            $readOnly={readOnly}
          />
        </FieldGroup>
        <FieldGroup $width='150px'>
          <FieldLabel>Type</FieldLabel>
          <SearchableSelect
            value={field.type}
            onChange={v => !readOnly && onUpdate && onUpdate({
              ...field,
              type: v,
              ...(v === 'audit_question' && (!(field.options || []).length || field.type === 'scored_question')
                ? { options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'na', label: 'N/A' }] }
                : {}),
              ...(v === 'scored_question' && field.type !== 'scored_question'
                ? { options: SCORED_QUESTION_OPTIONS }
                : {})
            })}
            options={FIELD_TYPE_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
            disabled={readOnly}
            searchPlaceholder='Search field types…'
            ariaLabel='Field type'
          />
        </FieldGroup>
      </FieldRow>

      <FieldRow>
        {(() => {
          const noPlaceholder = ['radio', 'checkbox', 'yes_no', 'multiselect', 'file', 'image', 'video', 'gps', 'checklist_upload', 'signature', 'audit_question', 'scored_question', 'statement'].includes(field.type)
          return (
            <FieldGroup $flex={1}>
              <FieldLabel style={noPlaceholder ? { opacity: 0.4 } : undefined}>(Optional) Input text that will be seen in the answer box</FieldLabel>
              <Input
                type='text'
                value={noPlaceholder ? '' : (field.placeholder || '')}
                onChange={e =>
                  !readOnly && !noPlaceholder && updateField('placeholder', e.target.value)
                }
                placeholder={noPlaceholder ? 'Not available for this type' : 'e.g. Enter your answer here...'}
                readOnly={readOnly || noPlaceholder}
                $readOnly={readOnly || noPlaceholder}
              />
            </FieldGroup>
          )
        })()}
        <CheckboxGroup>
          <CheckboxLabel $disabled={readOnly}>
            <input
              type='checkbox'
              checked={!!field.required}
              onChange={e =>
                !readOnly && updateField('required', e.target.checked)
              }
              disabled={readOnly}
            />
            Required
          </CheckboxLabel>
          <CheckboxLabel $disabled={readOnly}>
            <input
              type='checkbox'
              checked={!!field.readOnly}
              onChange={e =>
                !readOnly && updateField('readOnly', e.target.checked)
              }
              disabled={readOnly}
            />
            Read-only
          </CheckboxLabel>
          <CheckboxLabel $disabled={readOnly}>
            <input
              type='checkbox'
              checked={!!field.allowComment}
              onChange={e =>
                !readOnly && updateField('allowComment', e.target.checked)
              }
              disabled={readOnly}
            />
            Allow comment
          </CheckboxLabel>
          <CheckboxLabel $disabled={readOnly}>
            <input
              type='checkbox'
              checked={!!field.allowAttachments}
              onChange={e =>
                !readOnly && updateField('allowAttachments', e.target.checked)
              }
              disabled={readOnly}
            />
            Allow attachments
          </CheckboxLabel>
        </CheckboxGroup>
      </FieldRow>

      {(field.type === 'audit_question' || field.type === 'scored_question') && (
        <FieldRow>
          <FieldGroup $width='160px'>
            <FieldLabel>Question number</FieldLabel>
            <Input
              type='text'
              value={field.number || ''}
              onChange={e => !readOnly && updateField('number', e.target.value)}
              placeholder='e.g. 1.1'
              readOnly={readOnly}
              $readOnly={readOnly}
            />
          </FieldGroup>
          <FieldGroup $flex={1}>
            <FieldLabel>Answers</FieldLabel>
            <Input
              type='text'
              value={field.type === 'scored_question'
                ? '2 = Good standard, 1 = Needs improvement, 0 = Fail, or N/A (left out of the total), with comments, actions and photos'
                : 'Yes / No / N/A, with comments, actions and photos'}
              readOnly
              $readOnly
            />
          </FieldGroup>
        </FieldRow>
      )}

      {field.type === 'statement' && (
        <FieldRow>
          <FieldGroup $flex={1}>
            <FieldLabel>Text shown on the form</FieldLabel>
            <Input
              as='textarea'
              rows={6}
              value={field.description || ''}
              onChange={e => !readOnly && updateField('description', e.target.value)}
              placeholder='e.g. This is to confirm that a review took place of the above site…'
              readOnly={readOnly}
              $readOnly={readOnly}
            />
          </FieldGroup>
        </FieldRow>
      )}

      {field.type === 'yes_no' && (
        <YesNoPreview>
          <YesNoOption $disabled>
            <input type='radio' disabled /> Yes
          </YesNoOption>
          <YesNoOption $disabled>
            <input type='radio' disabled /> No
          </YesNoOption>
        </YesNoPreview>
      )}

      {needsOptions && (
        <OptionsBlock>
          <FieldLabel>Answer options</FieldLabel>
          {(field.options || []).map((opt, i) => (
            <OptionRow key={i}>
              <OptionInput
                type='text'
                value={opt.label || opt.value}
                onChange={e => {
                  if (readOnly) return
                  const text = e.target.value
                  const options = [...(field.options || [])]
                  options[i] = { ...options[i], label: text, value: text }
                  updateField('options', options)
                }}
                placeholder={`Option ${i + 1}`}
                readOnly={readOnly}
                $readOnly={readOnly}
              />
              {!readOnly && (
                <RemoveOptionBtn type='button' onClick={() => removeOption(i)}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </RemoveOptionBtn>
              )}
            </OptionRow>
          ))}
          {!readOnly && (
            <AddOptionBtn type='button' onClick={addOption}>
              <AddIcon style={{ fontSize: 14 }} /> Add Option
            </AddOptionBtn>
          )}
        </OptionsBlock>
      )}

      {field.type === 'file' && (
        <FileSettingsBlock>
          <FileSettingsHeader>
            <SettingsIcon style={{ fontSize: 16 }} />
            File Upload Settings
          </FileSettingsHeader>

          <FieldGroup>
            <FieldLabel>Accepted files</FieldLabel>
            <SearchableSelect
              value={field.fileAcceptMode || inferFileAcceptMode(field.acceptedMimeTypes)}
              onChange={v => {
                if (readOnly) return
                const mode = v
                updateField('fileAcceptMode', mode)
                updateField('acceptedMimeTypes', mode === 'photos_only' ? ['image/*'] : [])
              }}
              options={[
                { value: 'files_and_photos', label: 'Files & photos' },
                { value: 'photos_only', label: 'Photos only' },
                { value: 'files_only', label: 'Files only (no camera)' },
              ]}
              searchable={false}
              disabled={readOnly}
              ariaLabel='Accepted files'
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Max File Size (MB)</FieldLabel>
            <Input
              type='number'
              value={field.maxFileSizeMb != null ? field.maxFileSizeMb : 10}
              onChange={e =>
                !readOnly &&
                updateField(
                  'maxFileSizeMb',
                  e.target.value ? Number(e.target.value) : 10
                )
              }
              min={1}
              placeholder='10'
              readOnly={readOnly}
              $readOnly={readOnly}
            />
          </FieldGroup>

        </FileSettingsBlock>
      )}

      {field.type === 'checklist_upload' && (
        <FileSettingsBlock>
          <FileSettingsHeader>
            <SettingsIcon style={{ fontSize: 16 }} />
            Checklist Upload Action
          </FileSettingsHeader>

          <FieldHint>
            This field does not upload a file into the task form. It uses the
            task&apos;s checklist upload destination and shows an upload button
            plus a completion checkbox for the assignee.
          </FieldHint>
        </FileSettingsBlock>
      )}

      {/* B4: Field description / help text */}
      {!readOnly && (
        <FieldGroup style={{ marginTop: 8 }}>
          <FieldLabel>Help Text / Description</FieldLabel>
          <Input
            type='text'
            value={field.description || ''}
            onChange={e => updateField('description', e.target.value)}
            placeholder='Optional help text shown below the field'
          />
        </FieldGroup>
      )}

      {/* B2: Validation Rules */}
      {!readOnly &&
        !field.autoPopulated &&
        ['text', 'textarea', 'email', 'phone', 'number'].includes(
          field.type
        ) && (
          <ValidationBlock>
            <ValidationHeader>
              <RuleIcon style={{ fontSize: 16 }} />
              Validation Rules
            </ValidationHeader>
            <ValidationGrid>
              {['text', 'textarea', 'email', 'phone'].includes(field.type) && (
                <>
                  <FieldGroup $flex={1}>
                    <FieldLabel>Min Length</FieldLabel>
                    <Input
                      type='number'
                      value={field.validation?.minLength ?? ''}
                      onChange={e =>
                        updateField('validation', {
                          ...field.validation,
                          minLength: e.target.value
                            ? Number(e.target.value)
                            : undefined
                        })
                      }
                      placeholder='No minimum'
                      min={0}
                    />
                  </FieldGroup>
                  <FieldGroup $flex={1}>
                    <FieldLabel>Max Length</FieldLabel>
                    <Input
                      type='number'
                      value={field.validation?.maxLength ?? ''}
                      onChange={e =>
                        updateField('validation', {
                          ...field.validation,
                          maxLength: e.target.value
                            ? Number(e.target.value)
                            : undefined
                        })
                      }
                      placeholder='No maximum'
                      min={0}
                    />
                  </FieldGroup>
                </>
              )}
              {field.type === 'number' && (
                <>
                  <FieldGroup $flex={1}>
                    <FieldLabel>Min Value</FieldLabel>
                    <Input
                      type='number'
                      value={field.validation?.min ?? ''}
                      onChange={e =>
                        updateField('validation', {
                          ...field.validation,
                          min: e.target.value
                            ? Number(e.target.value)
                            : undefined
                        })
                      }
                      placeholder='No minimum'
                    />
                  </FieldGroup>
                  <FieldGroup $flex={1}>
                    <FieldLabel>Max Value</FieldLabel>
                    <Input
                      type='number'
                      value={field.validation?.max ?? ''}
                      onChange={e =>
                        updateField('validation', {
                          ...field.validation,
                          max: e.target.value
                            ? Number(e.target.value)
                            : undefined
                        })
                      }
                      placeholder='No maximum'
                    />
                  </FieldGroup>
                  <FieldGroup $flex={1}>
                    <FieldLabel>Tolerance %</FieldLabel>
                    <Input
                      type='number'
                      min={0}
                      max={100}
                      value={field.validation?.tolerancePct ?? ''}
                      onChange={e =>
                        updateField('validation', {
                          ...field.validation,
                          tolerancePct: e.target.value
                            ? Number(e.target.value)
                            : undefined
                        })
                      }
                      placeholder='Optional. Soft warning band'
                    />
                  </FieldGroup>
                </>
              )}
              {['text', 'textarea', 'email', 'phone'].includes(field.type) && (
                <FieldGroup $flex={1} style={{ gridColumn: '1 / -1' }}>
                  <FieldLabel>Regex Pattern</FieldLabel>
                  <Input
                    type='text'
                    value={field.validation?.pattern ?? ''}
                    onChange={e =>
                      updateField('validation', {
                        ...field.validation,
                        pattern: e.target.value || undefined
                      })
                    }
                    placeholder='e.g. ^[A-Z].*'
                  />
                  <FieldHint>
                    Regular expression pattern. Examples: ^[A-Z].* (starts with uppercase), ^\d{'{4}'}$ (exactly 4 digits)
                  </FieldHint>
                </FieldGroup>
              )}
            </ValidationGrid>
          </ValidationBlock>
        )}

      {/* B3: Auto-populate Mapping */}
      {!readOnly && !field.autoPopulated && (
        <AutoPopulateBlock>
          <AutoPopulateHeader>
            <AutoFixHighIcon style={{ fontSize: 16 }} />
            Auto-populate from BP Data
          </AutoPopulateHeader>
          <FieldGroup>
            <FieldLabel>Data Path</FieldLabel>
            <SearchableSelect
              value={field.autoPopulateSource || null}
              onChange={v =>
                updateField('autoPopulateSource', v || undefined)
              }
              options={[
                { value: 'name', label: 'Building Name' },
                { value: 'address', label: 'Building Address' },
                { value: 'number_of_floors', label: 'Number of Floors' },
                { value: 'number_of_units', label: 'Number of Units' },
                { value: 'building_type', label: 'Building Type' },
                { value: 'year_built', label: 'Year Built' },
              ]}
              clearable
              clearLabel='None (manual entry)'
              placeholder='None (manual entry)'
              ariaLabel='Data path'
            />
            <FieldHint>
              When set, this field will be auto-filled from the Building
              Passport data at task creation.
            </FieldHint>
          </FieldGroup>
        </AutoPopulateBlock>
      )}

      {/* Conditional Logic */}
      {!readOnly && (
        <ConditionalsBlock>
          <ConditionalsHeader
            onClick={() => setShowConditionals(!showConditionals)}
          >
            <ConditionalsTitle>
              <RuleIcon style={{ fontSize: 16 }} />
              Show/hide rules
              {field.conditionals?.length > 0 && (
                <ConditionalCount>{field.conditionals.length}</ConditionalCount>
              )}
            </ConditionalsTitle>
            {showConditionals ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ConditionalsHeader>

          {showConditionals && (
            <ConditionalsContent>
              {allFields.length === 0 && (
                <ConditionLabel style={{ padding: '8px 0', color: '#94a3b8' }}>
                  Add questions to create conditional rules.
                </ConditionLabel>
              )}
              {(field.conditionals || []).map((cond, i) => {
                const sourceField = getSourceField(cond.fieldKey)
                const sourceHasOptions =
                  sourceField &&
                  ['select', 'multiselect', 'radio', 'yes_no'].includes(sourceField.type)
                const needsValue = !['is_empty', 'is_not_empty'].includes(
                  cond.operator
                )

                return (
                  <ConditionalRow key={i}>
                    <ConditionalFields>
                      <ConditionalGroup>
                        <ConditionLabel>Show this question when</ConditionLabel>
                        <SearchableSelect
                          value={cond.fieldKey || null}
                          onChange={v =>
                            updateConditional(i, 'fieldKey', v)
                          }
                          options={availableSourceFields.map(f => ({ value: f.key, label: f.label || 'Untitled question' }))}
                          placeholder='Select a question...'
                          searchPlaceholder='Search questions…'
                          ariaLabel='Question'
                        />
                      </ConditionalGroup>

                      <ConditionalGroup>
                        <ConditionLabel>&nbsp;</ConditionLabel>
                        <SearchableSelect
                          value={cond.operator}
                          onChange={v =>
                            updateConditional(i, 'operator', v)
                          }
                          options={CONDITION_OPERATORS.map(op => ({ value: op.value, label: op.label }))}
                          searchable={false}
                          ariaLabel='Condition'
                        />
                      </ConditionalGroup>

                      {needsValue && (
                        <ConditionalGroup>
                          <ConditionLabel>&nbsp;</ConditionLabel>
                          {isListOperator(cond.operator) ? (
                            sourceHasOptions ? (
                              <ConditionValueChecklist>
                                {(sourceField.type === 'yes_no'
                                  ? [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]
                                  : (sourceField.options || [])
                                ).map(opt => {
                                  const chosen = toConditionList(cond.value)
                                  const ticked = chosen.includes(String(opt.value))
                                  return (
                                    <ConditionValueTick key={opt.value}>
                                      <input
                                        type='checkbox'
                                        checked={ticked}
                                        onChange={() =>
                                          updateConditional(
                                            i,
                                            'value',
                                            ticked
                                              ? chosen.filter(entry => entry !== String(opt.value))
                                              : [...chosen, String(opt.value)]
                                          )
                                        }
                                      />
                                      <span>{opt.label || opt.value}</span>
                                    </ConditionValueTick>
                                  )
                                })}
                              </ConditionValueChecklist>
                            ) : (
                              <>
                                <ConditionalInput
                                  type='text'
                                  value={toConditionList(cond.value).join(', ')}
                                  onChange={e =>
                                    updateConditional(i, 'value', toConditionList(e.target.value))
                                  }
                                  placeholder='e.g. 2, 3, 4'
                                />
                                <ConditionValueHint>
                                  Separate each answer with a comma.
                                </ConditionValueHint>
                              </>
                            )
                          ) : sourceHasOptions ? (
                            <SearchableSelect
                              value={toConditionScalar(cond.value) || null}
                              onChange={v =>
                                updateConditional(i, 'value', v)
                              }
                              options={(sourceField.type === 'yes_no' ? [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] : (sourceField.options || [])).map(opt => ({ value: opt.value, label: opt.label || opt.value }))}
                              placeholder='Select answer...'
                              ariaLabel='Answer'
                            />
                          ) : (
                            <ConditionalInput
                              type='text'
                              value={toConditionScalar(cond.value)}
                              onChange={e =>
                                updateConditional(i, 'value', e.target.value)
                              }
                              placeholder='Enter answer...'
                            />
                          )}
                        </ConditionalGroup>
                      )}
                    </ConditionalFields>

                    <RemoveConditionalBtn
                      type='button'
                      onClick={() => removeConditional(i)}
                    >
                      <CloseIcon style={{ fontSize: 14 }} />
                    </RemoveConditionalBtn>
                  </ConditionalRow>
                )
              })}

              {(field.conditionals || []).length >= 2 && (
                <ConditionLogicToggle>
                  <ConditionLogicLabel>Show it when</ConditionLogicLabel>
                  <ConditionLogicOption
                    $active={field.conditionLogic !== 'or'}
                    onClick={() => !readOnly && updateField('conditionLogic', 'and')}
                  >
                    all of these are true
                  </ConditionLogicOption>
                  <ConditionLogicOption
                    $active={field.conditionLogic === 'or'}
                    onClick={() => !readOnly && updateField('conditionLogic', 'or')}
                  >
                    any one is true
                  </ConditionLogicOption>
                </ConditionLogicToggle>
              )}

              <RuleSummary
                subject='question'
                action='show'
                logic={field.conditionLogic}
                parts={(field.conditionals || []).map(cond =>
                  describeCondition(cond, getSourceField(cond.fieldKey))
                )}
              />

              {allFields.length > 0 && (
                <AddConditionalBtn type='button' onClick={addConditional}>
                  <AddIcon style={{ fontSize: 14 }} /> Add another rule
                </AddConditionalBtn>
              )}
            </ConditionalsContent>
          )}
        </ConditionalsBlock>
      )}

      {!readOnly && (
        <FieldFooter>
          <FooterLeft>
            {onDuplicate && (
              <SecondaryBtn type='button' onClick={onDuplicate}>
                <ContentCopyIcon style={{ fontSize: 14 }} /> Duplicate
              </SecondaryBtn>
            )}
            {onDuplicateToSection && otherSections.length > 0 && (
              <DuplicateToWrap>
                <SecondaryBtn type='button' onClick={() => setShowCopyMenu(v => !v)}>
                  <ContentCopyIcon style={{ fontSize: 14 }} /> Duplicate to…
                  <ExpandMoreIcon style={{ fontSize: 16, marginLeft: -2 }} />
                </SecondaryBtn>
                {showCopyMenu && (
                  <>
                    <MenuBackdrop onClick={() => setShowCopyMenu(false)} />
                    <CopyMenu>
                      <CopyMenuLabel>Copy this question to</CopyMenuLabel>
                      {otherSections.map(s => (
                        <CopyMenuItem
                          key={s.key}
                          type='button'
                          onClick={() => {
                            onDuplicateToSection(s.key)
                            setShowCopyMenu(false)
                          }}
                        >
                          {s.title && s.title.trim() ? s.title : `Section ${s.index + 1}`}
                        </CopyMenuItem>
                      ))}
                    </CopyMenu>
                  </>
                )}
              </DuplicateToWrap>
            )}
          </FooterLeft>
          {!isOnly && (
            <DangerBtn type='button' onClick={onRemove}>
              <CloseIcon style={{ fontSize: 14 }} /> Remove Question
            </DangerBtn>
          )}
        </FieldFooter>
      )}
    </FieldCard>
  )
}

const SectionEditor = ({
  section,
  onUpdate,
  onRemove,
  isOnly,
  readOnly = false,
  allFields = [],
  sections = [],
  sectionKey,
  onCopyFieldToSection
}) => {
  const otherSections = sections.filter(s => s.key !== sectionKey)
  const [showSectionConditionals, setShowSectionConditionals] = useState(
    section.conditionals && section.conditionals.length > 0
  )
  const updateSection = (key, value) =>
    onUpdate && onUpdate({ ...section, [key]: value })

  const addSectionConditional = () => {
    const conditionals = [
      ...(section.conditionals || []),
      {
        fieldKey: '',
        operator: 'equals',
        value: '',
        action: 'show'
      }
    ]
    updateSection('conditionals', conditionals)
  }

  const updateSectionConditional = (index, key, value) => {
    const conditionals = [...(section.conditionals || [])]
    const next = { ...conditionals[index], [key]: value }
    if (key === 'fieldKey') {
      next.value = isListOperator(next.operator) ? [] : ''
    } else if (key === 'operator') {
      next.value = normaliseConditionValue(value, conditionals[index]?.value)
    }
    conditionals[index] = next
    updateSection('conditionals', conditionals)
  }

  const removeSectionConditional = index => {
    const conditionals = (section.conditionals || []).filter(
      (_, i) => i !== index
    )
    updateSection('conditionals', conditionals)
  }

  const getSourceField = fieldKey => allFields.find(f => f.key === fieldKey)

  const addField = () => {
    const fields = [...section.fields, createEmptyField()]
    updateSection('fields', fields)
  }

  const updateField = (index, updatedField) => {
    const fields = [...section.fields]
    fields[index] = updatedField
    updateSection('fields', fields)
  }

  const removeField = index => {
    const fields = section.fields.filter((_, i) => i !== index)
    updateSection('fields', fields)
  }

  const moveField = (index, direction) => {
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= section.fields.length) return
    const fields = [...section.fields]
    const temp = fields[index]
    fields[index] = fields[newIndex]
    fields[newIndex] = temp
    updateSection('fields', fields)
  }

  const duplicateField = index => {
    const original = section.fields[index]
    const clone = {
      ...JSON.parse(JSON.stringify(original)),
      key: generateKey('field'),
      label: (original.label || 'Untitled') + ' (copy)'
    }
    const fields = [...section.fields]
    fields.splice(index + 1, 0, clone)
    updateSection('fields', fields)
  }

  return (
    <SectionCard>
      <SectionHeader>
        <SectionInputGroup>
          <FieldLabel>Section Title</FieldLabel>
          <SectionTitleInput
            type='text'
            value={section.title}
            onChange={e => !readOnly && updateSection('title', e.target.value)}
            placeholder='Section title'
            readOnly={readOnly}
            $readOnly={readOnly}
          />
        </SectionInputGroup>
        <CheckboxLabel $standalone $disabled={readOnly}>
          <input
            type='checkbox'
            checked={!!section.repeatable}
            onChange={e =>
              !readOnly && updateSection('repeatable', e.target.checked)
            }
            disabled={readOnly}
          />
          Repeatable
        </CheckboxLabel>
      </SectionHeader>

      {section.repeatable && (
        <RepeatableSettings>
          <FieldGroup $flex={1}>
            <FieldLabel>Min entries</FieldLabel>
            <Input
              type='number'
              value={section.minEntries ?? 1}
              onChange={e =>
                !readOnly &&
                updateSection(
                  'minEntries',
                  e.target.value ? Number(e.target.value) : 1
                )
              }
              min={1}
              readOnly={readOnly}
              $readOnly={readOnly}
            />
          </FieldGroup>
          <FieldGroup $flex={1}>
            <FieldLabel>Max entries</FieldLabel>
            <Input
              type='number'
              value={section.maxEntries ?? ''}
              onChange={e =>
                !readOnly &&
                updateSection(
                  'maxEntries',
                  e.target.value ? Number(e.target.value) : null
                )
              }
              min={1}
              placeholder='No limit'
              readOnly={readOnly}
              $readOnly={readOnly}
            />
          </FieldGroup>
        </RepeatableSettings>
      )}

      <FieldGroup>
        <FieldLabel>Description</FieldLabel>
        <Input
          type='text'
          value={section.description || ''}
          onChange={e =>
            !readOnly && updateSection('description', e.target.value)
          }
          placeholder='Optional description'
          readOnly={readOnly}
          $readOnly={readOnly}
        />
      </FieldGroup>

      {!readOnly && (
        <ConditionalsHeader
          onClick={() => setShowSectionConditionals(!showSectionConditionals)}
        >
          <ConditionalsTitle>
            <RuleIcon style={{ fontSize: 16 }} />
            <span>
              Section Conditionals{' '}
              {(section.conditionals?.length || 0) > 0 &&
                `(${section.conditionals.length})`}
            </span>
          </ConditionalsTitle>
          {showSectionConditionals ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ConditionalsHeader>
      )}

      {showSectionConditionals && !readOnly && (
        <ConditionalsContent>
          {(section.conditionals || []).map((cond, ci) => {
            const sourceField = getSourceField(cond.fieldKey)
            const sourceHasOptions =
              sourceField &&
              ['select', 'multiselect', 'radio', 'yes_no'].includes(sourceField.type)
            return (
              <ConditionalRow key={ci}>
                <ConditionalFields>
                  <ConditionalGroup>
                    <ConditionLabel>What should happen</ConditionLabel>
                    <SearchableSelect
                      value={cond.action}
                      onChange={v =>
                        updateSectionConditional(ci, 'action', v)
                      }
                      options={[
                        { value: 'show', label: 'Show this section' },
                        { value: 'hide', label: 'Hide this section' },
                      ]}
                      searchable={false}
                      ariaLabel='What should happen'
                    />
                  </ConditionalGroup>

                  <ConditionalGroup>
                    <ConditionLabel>when</ConditionLabel>
                    <SearchableSelect
                      value={cond.fieldKey || null}
                      onChange={v =>
                        updateSectionConditional(ci, 'fieldKey', v)
                      }
                      options={allFields.map(f => ({ value: f.key, label: f.label || 'Untitled question' }))}
                      placeholder='Select question...'
                      searchPlaceholder='Search questions…'
                      ariaLabel='Question'
                    />
                  </ConditionalGroup>

                  <ConditionalGroup>
                    <ConditionLabel>&nbsp;</ConditionLabel>
                    <SearchableSelect
                      value={cond.operator}
                      onChange={v =>
                        updateSectionConditional(ci, 'operator', v)
                      }
                      options={CONDITION_OPERATORS.map(op => ({ value: op.value, label: op.label }))}
                      searchable={false}
                      ariaLabel='Condition'
                    />
                  </ConditionalGroup>

                  {!['is_empty', 'is_not_empty'].includes(cond.operator) && (
                    <ConditionalGroup>
                      <ConditionLabel>&nbsp;</ConditionLabel>
                      {isListOperator(cond.operator) ? (
                        sourceHasOptions ? (
                          <ConditionValueChecklist>
                            {(sourceField.type === 'yes_no'
                              ? [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]
                              : (sourceField.options || [])
                            ).map(opt => {
                              const chosen = toConditionList(cond.value)
                              const ticked = chosen.includes(String(opt.value))
                              return (
                                <ConditionValueTick key={opt.value}>
                                  <input
                                    type='checkbox'
                                    checked={ticked}
                                    onChange={() =>
                                      updateSectionConditional(
                                        ci,
                                        'value',
                                        ticked
                                          ? chosen.filter(entry => entry !== String(opt.value))
                                          : [...chosen, String(opt.value)]
                                      )
                                    }
                                  />
                                  <span>{opt.label || opt.value}</span>
                                </ConditionValueTick>
                              )
                            })}
                          </ConditionValueChecklist>
                        ) : (
                          <>
                            <ConditionalInput
                              value={toConditionList(cond.value).join(', ')}
                              onChange={e =>
                                updateSectionConditional(ci, 'value', toConditionList(e.target.value))
                              }
                              placeholder='e.g. 2, 3, 4'
                            />
                            <ConditionValueHint>
                              Separate each answer with a comma.
                            </ConditionValueHint>
                          </>
                        )
                      ) : sourceHasOptions ? (
                        <SearchableSelect
                          value={toConditionScalar(cond.value) || null}
                          onChange={v =>
                            updateSectionConditional(ci, 'value', v)
                          }
                          options={(sourceField.type === 'yes_no' ? [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] : (sourceField.options || [])).map(opt => ({ value: opt.value, label: opt.label || opt.value }))}
                          placeholder='Select answer...'
                          ariaLabel='Answer'
                        />
                      ) : (
                        <ConditionalInput
                          value={toConditionScalar(cond.value)}
                          onChange={e =>
                            updateSectionConditional(ci, 'value', e.target.value)
                          }
                          placeholder='Enter answer...'
                        />
                      )}
                    </ConditionalGroup>
                  )}
                </ConditionalFields>

                <RemoveConditionalBtn
                  type='button'
                  onClick={() => removeSectionConditional(ci)}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </RemoveConditionalBtn>
              </ConditionalRow>
            )
          })}
          {(section.conditionals || []).length >= 2 && (
            <ConditionLogicToggle>
              <ConditionLogicLabel>Apply when</ConditionLogicLabel>
              <ConditionLogicOption
                $active={section.conditionLogic !== 'or'}
                onClick={() => updateSection('conditionLogic', 'and')}
              >
                all of these are true
              </ConditionLogicOption>
              <ConditionLogicOption
                $active={section.conditionLogic === 'or'}
                onClick={() => updateSection('conditionLogic', 'or')}
              >
                any one is true
              </ConditionLogicOption>
            </ConditionLogicToggle>
          )}

          <RuleSummary
            subject='section'
            action={(section.conditionals || [])[0]?.action || 'show'}
            logic={section.conditionLogic}
            parts={(section.conditionals || []).map(cond =>
              describeCondition(cond, getSourceField(cond.fieldKey))
            )}
          />

          <AddConditionalBtn type='button' onClick={addSectionConditional}>
            <AddIcon style={{ fontSize: 14 }} /> Add another rule
          </AddConditionalBtn>
        </ConditionalsContent>
      )}

      <FieldsLabel>Questions</FieldsLabel>

      {section.fields.map((field, index) => (
        <FieldWrapper key={field.key}>
          {!readOnly && (
            <MoveButtons>
              <MoveBtn
                type='button'
                disabled={index === 0}
                onClick={() => moveField(index, -1)}
                title='Move up'
              >
                <ArrowUpwardIcon style={{ fontSize: 14 }} />
              </MoveBtn>
              <MoveBtn
                type='button'
                disabled={index === section.fields.length - 1}
                onClick={() => moveField(index, 1)}
                title='Move down'
              >
                <ArrowDownwardIcon style={{ fontSize: 14 }} />
              </MoveBtn>
            </MoveButtons>
          )}
          <FieldEditor
            field={field}
            onUpdate={readOnly ? null : updated => updateField(index, updated)}
            onRemove={readOnly ? null : () => removeField(index)}
            onDuplicate={readOnly ? null : () => duplicateField(index)}
            isOnly={section.fields.length === 1}
            readOnly={readOnly}
            allFields={allFields}
            otherSections={otherSections}
            onDuplicateToSection={
              readOnly || !onCopyFieldToSection
                ? null
                : targetKey => onCopyFieldToSection(field, targetKey)
            }
          />
        </FieldWrapper>
      ))}

      {!readOnly && (
        <SectionFooter>
          <SecondaryBtn type='button' onClick={addField} data-tour-id='formBuilder.add-question'>
            <AddIcon style={{ fontSize: 16 }} /> Add Question
          </SecondaryBtn>
          {!isOnly && (
            <DangerBtn type='button' onClick={onRemove}>
              <CloseIcon style={{ fontSize: 14 }} /> Remove Section
            </DangerBtn>
          )}
        </SectionFooter>
      )}
    </SectionCard>
  )
}

// --- AutomationRuleEditor ---

const AutomationRuleEditor = ({
  rule,
  onUpdate,
  onRemove,
  readOnly = false,
  allFields = [],
  availableTemplates = []
}) => {
  const updateRule = (key, value) =>
    onUpdate && onUpdate({ ...rule, [key]: value })

  const updateCondition = (key, value) => {
    const condition = { ...(rule.condition || {}), [key]: value }
    if (key === 'fieldKey') condition.value = ''
    updateRule('condition', condition)
  }

  const sourceField = allFields.find(f => f.key === rule.condition?.fieldKey)
  const sourceHasOptions =
    sourceField && ['select', 'multiselect', 'radio', 'yes_no'].includes(sourceField.type)
  const needsValue = !['is_empty', 'is_not_empty'].includes(
    rule.condition?.operator
  )

  return (
    <AutomationRuleCard>
      <FieldGroup>
        <FieldLabel>Rule Name</FieldLabel>
        <Input
          type='text'
          value={rule.name || ''}
          onChange={e => !readOnly && updateRule('name', e.target.value)}
          placeholder='e.g. Create specialist task'
          readOnly={readOnly}
          $readOnly={readOnly}
        />
      </FieldGroup>

      <AutomationConditionBlock>
        <ConditionLabel>WHEN</ConditionLabel>
        <ConditionalFields>
          <ConditionalGroup>
            <ConditionLabel>Field</ConditionLabel>
            <SearchableSelect
              value={rule.condition?.fieldKey || null}
              onChange={v =>
                !readOnly && updateCondition('fieldKey', v)
              }
              options={allFields.map(f => ({ value: f.key, label: f.label || 'Untitled question' }))}
              placeholder='Select question...'
              searchPlaceholder='Search questions…'
              disabled={readOnly}
              ariaLabel='Field'
            />
          </ConditionalGroup>

          <ConditionalGroup>
            <ConditionLabel>Condition</ConditionLabel>
            <SearchableSelect
              value={rule.condition?.operator || 'equals'}
              onChange={v =>
                !readOnly && updateCondition('operator', v)
              }
              options={CONDITION_OPERATORS.map(op => ({ value: op.value, label: op.label }))}
              searchable={false}
              disabled={readOnly}
              ariaLabel='Condition'
            />
          </ConditionalGroup>

          {needsValue && (
            <ConditionalGroup>
              <ConditionLabel>Value</ConditionLabel>
              {sourceHasOptions ? (
                <SearchableSelect
                  value={rule.condition?.value || null}
                  onChange={v =>
                    !readOnly && updateCondition('value', v)
                  }
                  options={(sourceField.type === 'yes_no' ? [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }] : (sourceField.options || [])).map(opt => ({ value: opt.value, label: opt.label || opt.value }))}
                  placeholder='Select value...'
                  disabled={readOnly}
                  ariaLabel='Value'
                />
              ) : (
                <ConditionalInput
                  type='text'
                  value={rule.condition?.value || ''}
                  onChange={e =>
                    !readOnly && updateCondition('value', e.target.value)
                  }
                  placeholder='Enter value...'
                  readOnly={readOnly}
                />
              )}
            </ConditionalGroup>
          )}
        </ConditionalFields>
      </AutomationConditionBlock>

      <AutomationConditionBlock>
        <ConditionLabel>THEN CREATE TASK</ConditionLabel>
        <FieldGroup>
          <FieldLabel>Task Title</FieldLabel>
          <Input
            type='text'
            value={rule.taskTitle || ''}
            onChange={e => !readOnly && updateRule('taskTitle', e.target.value)}
            placeholder='e.g. Help needed: {{source_task_title}}'
            readOnly={readOnly}
            $readOnly={readOnly}
          />
          {!readOnly && (
            <AutomationHint>
              Available placeholders: {'{{source_task_title}}'}, {'{{building_name}}'}, {'{{unit_name}}'}
            </AutomationHint>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Form Template</FieldLabel>
          <FormTemplateField
            value={rule.targetFormTemplateId || ''}
            template={availableTemplates.find(t => String(t.id) === String(rule.targetFormTemplateId)) || null}
            onChange={tpl => !readOnly && updateRule('targetFormTemplateId', tpl?.id || '')}
            disabled={readOnly}
            emptyLabel='No form chosen yet'
            emptyHint='Pick the form that evidences this remedial is done.'
          />
          <AutomationHint>Form not listed? Make sure it's published.</AutomationHint>
        </FieldGroup>

        <AutomationCheckboxGroup>
          <CheckboxLabel $disabled={readOnly}>
            <input
              type='checkbox'
              checked={!!rule.copyAssignees}
              onChange={e =>
                !readOnly && updateRule('copyAssignees', e.target.checked)
              }
              disabled={readOnly}
            />
            Copy assignees to new task
          </CheckboxLabel>
          <CheckboxLabel $disabled={readOnly}>
            <input
              type='checkbox'
              checked={!!rule.copyApprovers}
              onChange={e =>
                !readOnly && updateRule('copyApprovers', e.target.checked)
              }
              disabled={readOnly}
            />
            Copy approvers to new task
          </CheckboxLabel>
        </AutomationCheckboxGroup>
      </AutomationConditionBlock>

      {!readOnly && (
        <FieldFooter>
          <DangerBtn type='button' onClick={onRemove}>
            <CloseIcon style={{ fontSize: 14 }} /> Remove Rule
          </DangerBtn>
        </FieldFooter>
      )}
    </AutomationRuleCard>
  )
}

// --- Main FormBuilder ---

const FormBuilder = ({ template, onSave, saving = false, readOnly = false, formTemplates = [], fileTypes = [], saveLabel }) => {
  const [hidePreview, setHidePreview] = useState(false)
  const [name, setName] = useState(template?.name || '')
  const [description, setDescription] = useState(template?.description || '')
  const [sections, setSections] = useState(
    template?.schema?.sections || [createEmptySection()]
  )
  const [automationRules, setAutomationRules] = useState(
    template?.schema?.automationRules || []
  )
  const [renewalInterval, setRenewalInterval] = useState(template?.renewalInterval || '')
  const [renewalUnit, setRenewalUnit] = useState(template?.renewalUnit || 'month')
  const [category, setCategory] = useState(template?.category || '')
  const [fileTypeId, setFileTypeId] = useState(template?.fileTypeId || '')

  const availableTemplates = useMemo(() => {
    return (formTemplates || []).filter(t =>
      t.status === 'published' && t.id !== template?.id
    )
  }, [formTemplates, template?.id])

  const addAutomationRule = useCallback(() => {
    setAutomationRules(prev => [...prev, {
      key: generateKey('rule'),
      name: '',
      condition: { fieldKey: '', operator: 'equals', value: '' },
      targetFormTemplateId: '',
      taskTitle: '',
      copyAssignees: true,
      copyApprovers: false
    }])
  }, [])

  const updateAutomationRule = useCallback((index, updated) => {
    setAutomationRules(prev => {
      const newRules = [...prev]
      newRules[index] = updated
      return newRules
    })
  }, [])

  const removeAutomationRule = useCallback(index => {
    setAutomationRules(prev => prev.filter((_, i) => i !== index))
  }, [])

  const addSection = useCallback(() => {
    setSections(prev => [...prev, createEmptySection()])
  }, [])

  const addBpInfoSection = useCallback(() => {
    setSections(prev => [createBpInfoSection(), ...prev])
  }, [])

  const updateSection = useCallback((index, updated) => {
    setSections(prev => {
      const newSections = [...prev]
      newSections[index] = updated
      return newSections
    })
  }, [])

  const removeSection = useCallback(index => {
    setSections(prev => prev.filter((_, i) => i !== index))
  }, [])

  const moveSection = useCallback((index, direction) => {
    setSections(prev => {
      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= prev.length) return prev
      const newSections = [...prev]
      const temp = newSections[index]
      newSections[index] = newSections[newIndex]
      newSections[newIndex] = temp
      return newSections
    })
  }, [])

  const duplicateSection = useCallback(index => {
    setSections(prev => {
      const original = prev[index]
      if (!original) return prev
      const cloned = JSON.parse(JSON.stringify(original))
      const keyMap = {}
      const fields = (cloned.fields || []).map(field => {
        const newKey = generateKey('field')
        if (field.key) keyMap[field.key] = newKey
        return { ...field, key: newKey }
      })
      const remapConditionals = list =>
        (list || []).map(cond =>
          cond && cond.fieldKey && keyMap[cond.fieldKey]
            ? { ...cond, fieldKey: keyMap[cond.fieldKey] }
            : cond
        )
      const duplicated = {
        ...cloned,
        key: generateKey('section'),
        title: `${original.title || 'Untitled section'} (copy)`,
        fields: fields.map(field => ({
          ...field,
          conditionals: remapConditionals(field.conditionals)
        })),
        conditionals: remapConditionals(cloned.conditionals)
      }
      const newSections = [...prev]
      newSections.splice(index + 1, 0, duplicated)
      return newSections
    })
  }, [])

  const copyFieldToSection = useCallback((field, targetSectionKey) => {
    if (!field || !targetSectionKey) return
    setSections(prev => {
      const targetIndex = prev.findIndex(s => s.key === targetSectionKey)
      if (targetIndex === -1) return prev
      const clone = {
        ...JSON.parse(JSON.stringify(field)),
        key: generateKey('field')
      }
      const newSections = [...prev]
      const target = newSections[targetIndex]
      newSections[targetIndex] = {
        ...target,
        fields: [...(target.fields || []), clone]
      }
      return newSections
    })
  }, [])

  const handleSave = () => {
    if (!onSave) return

    // Clean sections before saving: strip empty options and incomplete conditionals
    const cleanedSections = sections.map((s, i) => ({
      ...s,
      position: i,
      fields: (s.fields || []).map(f => ({
        ...f,
        options: (f.options || []).filter(opt => (opt.label || opt.value || '').trim()),
        conditionals: (f.conditionals || []).filter(c => c.fieldKey && c.operator)
      })),
      conditionals: (s.conditionals || []).filter(c => c.fieldKey && c.operator)
    }))

    const schemaExtras = Object.fromEntries(
      Object.entries(template?.schema || {}).filter(([key]) => !['sections', 'automationRules'].includes(key))
    )
    const schema = {
      ...schemaExtras,
      sections: cleanedSections,
      ...(automationRules.length > 0 ? { automationRules } : {})
    }

    // Build auto_populate_map from fields with autoPopulateSource
    const autoPopulateMap = {}
    for (const section of sections) {
      for (const field of section.fields || []) {
        if (field.autoPopulateSource) {
          autoPopulateMap[field.key] = field.autoPopulateSource
        }
      }
    }

    onSave({
      name,
      description: description || null,
      schema,
      autoPopulateMap: Object.keys(autoPopulateMap).length > 0 ? autoPopulateMap : null,
      renewalInterval: renewalInterval ? parseInt(renewalInterval) : null,
      renewalUnit: renewalInterval ? renewalUnit : null,
      category: category || null,
      fileTypeId: fileTypeId || null
    })
  }

  const totalFields = sections.reduce((sum, s) => sum + (s.fields?.filter(f => f.type !== 'statement')?.length || 0), 0)
  const requiredFields = sections.reduce(
    (sum, s) => sum + (s.fields?.filter(f => f.required)?.length || 0),
    0
  )

  // Collect all fields from all sections for conditional logic
  const allFields = useMemo(() => {
    return sections.flatMap(s => s.fields || [])
  }, [sections])

  const sectionSummaries = useMemo(
    () => sections.map((s, i) => ({ key: s.key, title: s.title, index: i })),
    [sections]
  )

  const hasBpSection = sections.some(s => s.fields?.some(f => f.autoPopulated))
  const canSave = name.trim() && sections.length > 0 && !saving

  return (
    <SideBySideLayout $hasPreview={!readOnly && !hidePreview}>
      <EditorPane $fullWidth={readOnly || hidePreview}>
      <>
      {/* Template Name & Description */}
      <TopSection>
        <FieldGroup>
          <FieldLabel $bold>Template Name</FieldLabel>
          <NameInput
            type='text'
            value={name}
            onChange={e => !readOnly && setName(e.target.value)}
            placeholder='Enter template name'
            readOnly={readOnly}
            $readOnly={readOnly}
            data-tour-id='formBuilder.name'
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel $bold>Description</FieldLabel>
          <DescriptionTextarea
            value={description}
            onChange={e => !readOnly && setDescription(e.target.value)}
            placeholder='Optional description'
            readOnly={readOnly}
            $readOnly={readOnly}
          />
        </FieldGroup>
        <FieldGroup>
          <FieldLabel $bold>Category</FieldLabel>
          <SearchableSelect
            value={category || null}
            onChange={v => !readOnly && setCategory(v || '')}
            options={FORM_CATEGORIES.map(c => ({ value: c.value, label: c.label }))}
            clearable
            clearLabel='Uncategorised'
            placeholder='Uncategorised'
            searchPlaceholder='Search categories…'
            disabled={readOnly}
            ariaLabel='Category'
          />
        </FieldGroup>
      </TopSection>

      {/* Stats Bar */}
      <StatsBar>
        <StatItem>Sections: <strong>{sections.length}</strong></StatItem>
        <StatDivider />
        <StatItem>Questions: <strong>{totalFields}</strong></StatItem>
        <StatDivider />
        <StatItem>Required: <strong>{requiredFields}</strong></StatItem>
        {totalFields > 50 && (
          <span style={{ color: '#b45309', fontSize: '12px' }}>Large forms may impact performance</span>
        )}
        {sections.length > 15 && (
          <span style={{ color: '#b45309', fontSize: '12px' }}>Consider splitting into multiple templates</span>
        )}
      </StatsBar>

      {/* Add Building Info - top placement */}
      {!readOnly && !hasBpSection && (
        <BpInfoBtn type='button' onClick={addBpInfoSection} style={{ marginBottom: 12 }}>
          <ApartmentIcon style={{ fontSize: 16 }} /> Add Building Info
        </BpInfoBtn>
      )}

      {/* Sections */}
      {sections.map((section, index) => (
        <SectionWrapper key={section.key}>
          <SectionLabel>
            <SectionAccent />
            Section {index + 1}
            {!readOnly && (
              <SectionHeaderActions>
                <DuplicateSectionBtn
                  type='button'
                  onClick={() => duplicateSection(index)}
                  title='Duplicate this section'
                >
                  <ContentCopyIcon style={{ fontSize: 14 }} /> Duplicate
                </DuplicateSectionBtn>
                <SectionMoveButtons>
                  <MoveBtn
                    type='button'
                    disabled={index === 0}
                    onClick={() => moveSection(index, -1)}
                    title='Move up'
                  >
                    <ArrowUpwardIcon style={{ fontSize: 14 }} />
                  </MoveBtn>
                  <MoveBtn
                    type='button'
                    disabled={index === sections.length - 1}
                    onClick={() => moveSection(index, 1)}
                    title='Move down'
                  >
                    <ArrowDownwardIcon style={{ fontSize: 14 }} />
                  </MoveBtn>
                </SectionMoveButtons>
              </SectionHeaderActions>
            )}
          </SectionLabel>
          <SectionEditor
            section={section}
            onUpdate={readOnly ? null : (updated => updateSection(index, updated))}
            onRemove={readOnly ? null : (() => removeSection(index))}
            isOnly={sections.length === 1}
            readOnly={readOnly}
            allFields={allFields}
            sections={sectionSummaries}
            sectionKey={section.key}
            onCopyFieldToSection={readOnly ? null : copyFieldToSection}
          />
        </SectionWrapper>
      ))}

      {/* Add Section / Building Info */}
      {!readOnly && (
        <ButtonGroup style={{ marginBottom: 16 }}>
          <SecondaryBtn type='button' onClick={addSection}>
            <AddIcon style={{ fontSize: 16 }} /> Add Section
          </SecondaryBtn>
          {!hasBpSection && (
            <BpInfoBtn type='button' onClick={addBpInfoSection}>
              <ApartmentIcon style={{ fontSize: 16 }} /> Add Building Info
            </BpInfoBtn>
          )}
        </ButtonGroup>
      )}

      {/* Automation Rules */}
      {(!readOnly || automationRules.length > 0) && (
        <AutomationSection>
          <AutomationHeader>
            <AutoFixHighIcon style={{ fontSize: 20 }} />
            <span>Automation Rules</span>
            {automationRules.length > 0 && (
              <ConditionalCount>{automationRules.length}</ConditionalCount>
            )}
          </AutomationHeader>
          <AutomationDescription>
            Automatically create tasks when conditions are met during form submission.
          </AutomationDescription>

          {automationRules.map((rule, index) => (
            <AutomationRuleEditor
              key={rule.key}
              rule={rule}
              onUpdate={readOnly ? null : (updated => updateAutomationRule(index, updated))}
              onRemove={readOnly ? null : (() => removeAutomationRule(index))}
              readOnly={readOnly}
              allFields={allFields}
              availableTemplates={availableTemplates}
            />
          ))}

          {!readOnly && automationRules.length < 10 && (
            <AddConditionalBtn type='button' onClick={addAutomationRule}>
              <AddIcon style={{ fontSize: 14 }} /> Add Automation Rule
            </AddConditionalBtn>
          )}
        </AutomationSection>
      )}

      {/* Renewal Settings */}
      {!readOnly && (
        <RenewalSection>
          <AutomationHeader>
            <RepeatIcon style={{ fontSize: 20 }} />
            <span>Auto-Renewal</span>
          </AutomationHeader>
          <AutomationDescription>
            Automatically create tasks on a schedule when this template is assigned to Building Passports.
          </AutomationDescription>
          <RenewalRow>
            <FieldLabel>Renew every</FieldLabel>
            <RenewalInput
              type="number"
              min="1"
              value={renewalInterval}
              onChange={e => setRenewalInterval(e.target.value)}
              placeholder="—"
            />
            <RenewalSelect
              value={renewalUnit}
              onChange={v => setRenewalUnit(v)}
              options={[
                { value: 'month', label: 'Month(s)' },
                { value: 'year', label: 'Year(s)' },
              ]}
              searchable={false}
              ariaLabel='Renewal unit'
            />
            {renewalInterval && (
              <RenewalClearBtn type="button" onClick={() => { setRenewalInterval(''); setRenewalUnit('month') }}>
                <CloseIcon style={{ fontSize: 14 }} />
              </RenewalClearBtn>
            )}
          </RenewalRow>
          {!renewalInterval && (
            <RenewalHint>Leave empty for no auto-renewal.</RenewalHint>
          )}
        </RenewalSection>
      )}
      {readOnly && template?.renewalInterval && (
        <RenewalSection>
          <AutomationHeader>
            <RepeatIcon style={{ fontSize: 20 }} />
            <span>Auto-Renewal</span>
          </AutomationHeader>
          <RenewalHint>
            Renews every {template.renewalInterval} {template.renewalUnit}{template.renewalInterval > 1 ? 's' : ''}.
          </RenewalHint>
        </RenewalSection>
      )}

      {/* Report Document Type */}
      {!readOnly && (
        <FileTypeSection>
          <AutomationHeader>
            <FolderOpenIcon style={{ fontSize: 20 }} />
            <span>Report Document Type</span>
          </AutomationHeader>
          <AutomationDescription>
            When set, completed task reports will be saved as documents of this type in the Building Passport.
          </AutomationDescription>
          <FileTypeRow>
            <FieldLabel>Document type</FieldLabel>
            <Autocomplete
              options={fileTypes || []}
              getOptionLabel={(ft) => ft?.name || ''}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={(fileTypes || []).find(ft => ft.id === fileTypeId) || null}
              onChange={(_, ft) => setFileTypeId(ft?.id || '')}
              size="small"
              sx={{ flex: 1, minWidth: 220 }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search for a document type…" />
              )}
              noOptionsText="No matching document types"
            />
          </FileTypeRow>
        </FileTypeSection>
      )}
      {readOnly && template?.fileTypeId && (
        <FileTypeSection>
          <AutomationHeader>
            <FolderOpenIcon style={{ fontSize: 20 }} />
            <span>Report Document Type</span>
          </AutomationHeader>
          <FileTypeHint>
            Reports are saved as: {template.fileType?.name || 'Configured document type'}
          </FileTypeHint>
        </FileTypeSection>
      )}

      {/* Bottom Actions */}
      {!readOnly && (
        <BottomActions>
          <PrimaryBtn type='button' onClick={handleSave} disabled={!canSave} data-tour-id='formBuilder.save'>
            {saving
              ? 'Saving…'
              : saveLabel || (template
                ? 'Update Template'
                : 'Create Template')}
          </PrimaryBtn>
        </BottomActions>
      )}
      </>
      </EditorPane>

      {!readOnly && (
        hidePreview ? (
          <CollapsedPreview>
            <PreviewToggle onClick={() => setHidePreview(false)} title="Show Preview">
              <VisibilityIcon style={{ fontSize: 16 }} />
            </PreviewToggle>
          </CollapsedPreview>
        ) : (
          <PreviewPane>
            <PreviewSticky>
              <PreviewHeader>
                <PreviewHeaderTop>
                  <PreviewTitle>{name || 'Untitled Template'}</PreviewTitle>
                  <PreviewToggle onClick={() => setHidePreview(true)} title="Hide Preview">
                    <CloseIcon style={{ fontSize: 14 }} />
                  </PreviewToggle>
                </PreviewHeaderTop>
                {description && <PreviewDescription>{description}</PreviewDescription>}
              </PreviewHeader>
              <PreviewScroll>
                <FormRenderer
                  schema={{ sections }}
                  responses={{}}
                  onAutoSave={() => {}}
                  readOnly={false}
                  showAllSections
                />
              </PreviewScroll>
            </PreviewSticky>
          </PreviewPane>
        )
      )}
    </SideBySideLayout>
  )
}

export default FormBuilder

// --- Styled Components ---

const SideBySideLayout = styled.div`
  display: flex;
  gap: 24px;
  align-items: stretch;
  max-width: ${({ $hasPreview }) => $hasPreview ? 'none' : '900px'};
  margin: 0 auto;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const EditorPane = styled.div`
  flex: 1;
  min-width: 0;
  max-width: ${({ $fullWidth }) => $fullWidth ? '900px' : 'none'};
  margin: ${({ $fullWidth }) => $fullWidth ? '0 auto' : '0'};
`

const PreviewPane = styled.div`
  width: 340px;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    order: -1;
  }
`

const PreviewSticky = styled.div`
  position: sticky;
  top: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
`

const PreviewHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
`

const PreviewHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const PreviewTitle = styled.h2`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PreviewDescription = styled.p`
  font-size: 11px;
  color: #64748b;
  margin: 4px 0 0;
`

const CollapsedPreview = styled.div`
  flex-shrink: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
`

const PreviewScroll = styled.div`
  padding: 16px;
  font-size: 13px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  & > * {
    transform: scale(0.85);
    transform-origin: top left;
    width: 117.6%;
  }
`

const PreviewToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #94a3b8;
  background: white;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    color: #1e293b;
    border-color: #cbd5e1;
    background: #f8fafc;
  }
`


const TopSection = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FieldGroup = styled.div`
  flex: ${({ $flex }) => $flex || 'unset'};
  width: ${({ $width }) => $width || 'auto'};
  margin-bottom: ${({ $mb }) => $mb || '0'};

  @media (max-width: 768px) {
    width: 100%;
  }
`

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: ${({ $bold }) => ($bold ? '600' : '500')};
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 13px;
  color: ${({ theme, $readOnly }) =>
    $readOnly ? theme.colors.grey?.[800] || '#666' : theme.colors.haiti};
  background: ${({ $readOnly }) => ($readOnly ? '#f5f5f5' : 'white')};
  box-sizing: border-box;
  transition: border-color 0.2s;
  outline: none;
  cursor: ${({ $readOnly }) => ($readOnly ? 'not-allowed' : 'text')};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }

  &:focus {
    border-color: ${({ $readOnly, theme }) =>
      $readOnly ? theme.colors.mediumGrey : theme.colors.secondary};
    box-shadow: ${({ $readOnly, theme }) =>
      $readOnly ? 'none' : `0 0 0 2px ${theme.colors.secondary}20`};
  }
`

const NameInput = styled(Input)`
  padding: 10px 14px;
  font-size: 15px;
  font-weight: 500;
`

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme, $readOnly }) =>
    $readOnly ? theme.colors.grey?.[800] || '#666' : theme.colors.haiti};
  background: ${({ $readOnly }) => ($readOnly ? '#f5f5f5' : 'white')};
  min-height: 60px;
  resize: ${({ $readOnly }) => ($readOnly ? 'none' : 'vertical')};
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;
  cursor: ${({ $readOnly }) => ($readOnly ? 'not-allowed' : 'text')};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }

  &:focus {
    border-color: ${({ $readOnly, theme }) =>
      $readOnly ? theme.colors.mediumGrey : theme.colors.secondary};
    box-shadow: ${({ $readOnly, theme }) =>
      $readOnly ? 'none' : `0 0 0 2px ${theme.colors.secondary}20`};
  }
`

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding: 12px 18px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 8px;
    padding: 10px 14px;
  }
`

const StatItem = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};

  strong {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const StatDivider = styled.span`
  width: 1px;
  height: 16px;
  background: ${({ theme }) => theme.colors.mediumGrey};
`

const SectionWrapper = styled.div`
  margin-bottom: 24px;
`

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 8px;
`

const SectionAccent = styled.span`
  width: 4px;
  height: 16px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 2px;
`

const SectionMoveButtons = styled.div`
  display: flex;
  gap: 4px;
`

const SectionHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`

const DuplicateSectionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 600;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const SectionCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  padding: 20px;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    padding: 14px;
  }
`

const SectionHeader = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
  align-items: flex-end;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`

const SectionInputGroup = styled.div`
  flex: 1;
`

const RepeatableSettings = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
`

const SectionTitleInput = styled(Input)`
  font-size: 14px;
  font-weight: 500;
  padding: 10px 14px;
`

const FieldsLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 16px 0 10px;
`

const FieldWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const MoveButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;

  @media (max-width: 768px) {
    flex-direction: row;
    padding-top: 0;
  }
`

const MoveBtn = styled.button`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  color: ${({ theme }) => theme.colors.haiti};
  transition: all 0.15s;
  padding: 0;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`

const FieldCard = styled.div`
  flex: 1;
  padding: 14px;
  border: 1px solid
    ${({ theme, $readOnly, $autoPopulated }) =>
      $autoPopulated
        ? '#bae6fd'
        : $readOnly
          ? theme.colors.mediumGrey
          : theme.colors.lightGrey};
  border-radius: 8px;
  background: ${({ theme, $readOnly, $autoPopulated }) =>
    $autoPopulated
      ? '#f0f9ff'
      : $readOnly
        ? '#f9f9f9'
        : theme.colors.grey?.[400] || '#fafafa'};
  opacity: ${({ $readOnly }) => ($readOnly ? 0.85 : 1)};
  min-width: 0;

  @media (max-width: 768px) {
    padding: 10px;
  }
`

const AutoPopulatedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #0369a1;
  margin-bottom: 10px;
  padding: 4px 10px;
  background: #e0f2fe;
  border-radius: 4px;
  width: fit-content;

  span {
    color: #0284c7;
    font-weight: 400;
  }
`

const TaskContextBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #6d28d9;
  margin-bottom: 10px;
  padding: 4px 10px;
  background: #ede9fe;
  border-radius: 4px;
  width: fit-content;
`

const FieldRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  padding-bottom: 4px;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 10px;
    padding-top: 4px;
  }
`

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  white-space: nowrap;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  ${({ $standalone }) => $standalone && 'padding: 4px 0;'}

  input[type='checkbox'] {
    accent-color: ${({ theme }) => theme.colors.primary};
    width: 15px;
    height: 15px;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  }
`

const YesNoPreview = styled.div`
  display: flex;
  gap: 16px;
  padding: 8px 0;
`

const YesNoOption = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
  cursor: default;

  input {
    cursor: default;
  }
`

const OptionsBlock = styled.div`
  margin-bottom: 10px;
`

const OptionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`

const OptionInput = styled.input`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 4px;
  font-size: 12px;
  color: ${({ theme, $readOnly }) =>
    $readOnly ? theme.colors.grey?.[800] || '#666' : theme.colors.haiti};
  background: ${({ $readOnly }) => ($readOnly ? '#f5f5f5' : 'white')};
  box-sizing: border-box;
  outline: none;
  cursor: ${({ $readOnly }) => ($readOnly ? 'not-allowed' : 'text')};

  &:focus {
    border-color: ${({ $readOnly, theme }) =>
      $readOnly ? theme.colors.mediumGrey : theme.colors.secondary};
  }
`

const RemoveOptionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error || '#d14925'};
  cursor: pointer;
  border-radius: 4px;
  padding: 0;
  transition: background 0.15s;

  &:hover {
    background: rgba(209, 73, 37, 0.08);
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`

const AddOptionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => `${theme.colors.secondary}10`};
  }
`

const FileSettingsBlock = styled.div`
  margin-top: 12px;
  margin-bottom: 10px;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.linen || '#fbede9'};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.linen || '#fbede9'};
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FileSettingsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const ValidationBlock = styled.div`
  margin-top: 12px;
  margin-bottom: 10px;
  padding: 14px;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  background: #eef2ff;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const ValidationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #4338ca;

  svg {
    color: #4338ca;
  }
`

const ValidationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const AutoPopulateBlock = styled.div`
  margin-top: 12px;
  margin-bottom: 10px;
  padding: 14px;
  border: 1px solid #d1fae5;
  border-radius: 8px;
  background: #ecfdf5;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AutoPopulateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #059669;

  svg {
    color: #059669;
  }
`

const FieldHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
  margin-top: 4px;
`

const FieldFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
`

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: auto;
`

const DuplicateToWrap = styled.div`
  position: relative;
  display: inline-flex;
`

const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
`

const CopyMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  z-index: 50;
  min-width: 210px;
  max-height: 240px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 6px;
`

const CopyMenuLabel = styled.div`
  padding: 4px 10px 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
`

const CopyMenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`

const SectionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 14px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`

const BottomActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`

const SecondaryBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    color: white;
  }
`

const DangerBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error || '#d14925'};
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.error || '#d14925'};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.error || '#d14925'};
    color: white;
  }
`

const PrimaryBtn = styled.button`
  padding: 10px 28px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: ${({ disabled, theme }) =>
    disabled ? theme.colors.grey?.[500] || '#e6e6e6' : theme.colors.haiti};
  border: 2px solid
    ${({ disabled, theme }) =>
      disabled ? theme.colors.grey?.[600] || '#ccc' : theme.colors.secondary};
  border-radius: 20px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  box-shadow: ${({ disabled }) =>
    disabled ? 'none' : '0 0 15px rgba(41, 152, 163, 0.2)'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.secondary};
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

const BpInfoBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  color: #0369a1;
  background: #f0f9ff;
  border: 1px dashed #7dd3fc;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #e0f2fe;
    border-color: #38bdf8;
  }
`

// Conditional Logic Styles
const ConditionalsBlock = styled.div`
  margin-top: 12px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  overflow: hidden;
`

const ConditionalsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.lightGrey};
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }

  svg {
    font-size: 20px;
    color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
  }
`

const ConditionalsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    font-size: 16px;
    color: ${({ theme }) => theme.colors.secondary};
  }
`

const ConditionalCount = styled.span`
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
`

const ConditionalsContent = styled.div`
  padding: 14px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    padding: 10px;
    gap: 10px;
  }
`

const ConditionalRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 12px;
  background: white;
  border-left: 3px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`

const ConditionalFields = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`

const ConditionalGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  flex: 1;

  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`

const ConditionLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
  text-transform: uppercase;
`

const RuleSummaryBox = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #e6f7f9;
  border: 1px solid #bfe9ee;
`

const RuleSummaryHeading = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2aa6b3;
  margin-bottom: 3px;
`

const RuleSummaryText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #0f172a;
`

const RuleSummaryAside = styled.span`
  color: #475569;
`

const ConditionValueChecklist = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  background: #fff;
`

const ConditionValueTick = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #0f172a;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.secondary};
  }
`

const ConditionValueHint = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
`

const ConditionalInput = styled.input`
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  background: white;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const RemoveConditionalBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  color: ${({ theme }) => theme.colors.error || '#d14925'};
  cursor: pointer;
  border-radius: 6px;
  flex-shrink: 0;
  align-self: flex-end;
  margin-bottom: 4px;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.error || '#d14925'};
    border-color: ${({ theme }) => theme.colors.error || '#d14925'};
    color: white;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    margin-top: 0;
    align-self: flex-end;
  }
`

const AddConditionalBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  background: none;
  border: 1px dashed ${({ theme }) => theme.colors.secondary};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => `${theme.colors.secondary}10`};
  }
`

// Automation Rules Styles
const AutomationSection = styled.div`
  margin-top: 24px;
  padding: 20px;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  background: #fffbeb;

  @media (max-width: 768px) {
    padding: 14px;
    margin-top: 16px;
  }
`

const AutomationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 6px;

  svg {
    color: #f59e0b;
  }
`

const AutomationDescription = styled.p`
  font-size: 13px;
  color: #b45309;
  margin: 0 0 16px 0;
`

const AutomationRuleCard = styled.div`
  padding: 16px;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  background: white;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AutomationConditionBlock = styled.div`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const AutomationCheckboxGroup = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`

const AutomationHint = styled.div`
  font-size: 11px;
  color: #92400e;
  margin-top: 4px;
  font-style: italic;
`

const RenewalSection = styled.div`
  margin-top: 24px;
  padding: 20px;
  border: 1px solid #a78bfa;
  border-radius: 8px;
  background: #f5f3ff;

  @media (max-width: 768px) {
    padding: 14px;
    margin-top: 16px;
  }
`

const RenewalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const RenewalInput = styled.input`
  width: 70px;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #c4b5fd;
  border-radius: 6px;
  outline: none;
  text-align: center;
  background: white;
  box-sizing: border-box;

  &:focus {
    border-color: #7c3aed;
    box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
  }
`

const RenewalSelect = styled(SearchableSelect)`
  width: 140px;
`

const RenewalClearBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid #c4b5fd;
  border-radius: 50%;
  background: white;
  color: #7c3aed;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #7c3aed;
    color: white;
  }
`

const RenewalHint = styled.div`
  font-size: 12px;
  color: #6d28d9;
  margin-top: 6px;
`

const FileTypeSection = styled.div`
  margin-top: 24px;
  padding: 20px;
  border: 1px solid #5eead4;
  border-radius: 8px;
  background: #f0fdfa;

  @media (max-width: 768px) {
    padding: 14px;
    margin-top: 16px;
  }
`

const FileTypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const FileTypeHint = styled.div`
  font-size: 12px;
  color: #0d9488;
  margin-top: 6px;
`

const ConditionLogicToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  margin-bottom: 4px;
`

const ConditionLogicLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  margin-right: 4px;
`

const ConditionLogicOption = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${({ $active }) => $active ? '#c84a31' : '#e2e8f0'};
  background: ${({ $active }) => $active ? '#fef2f2' : '#fff'};
  color: ${({ $active }) => $active ? '#c84a31' : '#64748b'};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #c84a31;
  }
`
